import React, { useState } from 'react'
import { Currency, Token } from '@nominex/nomiswap-sdk'
import { ErrorIcon, Grid, Link, Message, Tag } from '@nominex/dex-uikit'
import { SlButton, SlCheckbox } from '@shoelace-style/shoelace/dist/react'
import { AutoColumn } from 'components/Layout'
import { useAddUserToken } from 'state/user/hooks'
import { getBlockExplorerLink, getBlockExplorerName } from 'utils'
import truncateHash from 'utils/truncateHash'
import { useWagmi } from 'libs/wagmi'
import { useCombinedInactiveList } from 'state/lists/hooks'
import { ListLogo } from 'components/Logo'
import { useTranslation } from 'contexts/Localization'

interface ImportProps {
  tokens: Token[]
  handleCurrencySelect?: (currency: Currency) => void
}

function ImportToken({ tokens, handleCurrencySelect }: ImportProps) {
  const { chainId } = useWagmi()

  const { t } = useTranslation()

  const [confirmed, setConfirmed] = useState(false)

  const addToken = useAddUserToken()

  // use for showing import source on inactive tokens
  const inactiveTokenList = useCombinedInactiveList()

  return (
    <AutoColumn gap="lg" px="24px">
      <Message variant="warning">
        <p className="text-primary">
          {t(
            'Anyone can create a BEP20 token on BSC with any name, including creating fake versions of existing tokens and tokens that claim to represent projects that do not have a token.',
          )}
          <br />
          <br />
          {t('If you purchase an arbitrary token, you may be unable to sell it back.')}
        </p>
      </Message>

      {tokens.map((token) => {
        const list = chainId && inactiveTokenList?.[chainId]?.[token.address]?.list
        const address = token.address ? `${truncateHash(token.address)}` : null
        return (
          <Grid key={token.address} gridTemplateRows="1fr 1fr 1fr" gridGap="4px">
            {list !== undefined ? (
              <Tag
                variant="success"
                outline
                scale="sm"
                startIcon={list.logoURI && <ListLogo logoURI={list.logoURI} size="12px" />}
              >
                {t('via')} {list.name}
              </Tag>
            ) : (
              <Tag variant="failure" outline scale="sm" startIcon={<ErrorIcon color="failure" />}>
                {t('Unknown Source')}
              </Tag>
            )}
            <div className="flex items-center gap-8">
              <p className="text-primary">{token.name}</p>
              <p className="text-primary">({token.symbol})</p>
            </div>
            {chainId && (
              <div className="flex justify-between w-[100%] gap-4">
                <p className="text-primary">{address}</p>
                <Link href={getBlockExplorerLink(token.address, 'address', chainId)} external>
                  (
                  {t('View on %site%', {
                    site: getBlockExplorerName(chainId),
                  })}
                  )
                </Link>
              </div>
            )}
          </Grid>
        )
      })}

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-8">
          <SlCheckbox name="confirmed" checked={confirmed} onSlChange={() => setConfirmed(!confirmed)} />
          <p className="text-primary select-none">{t('I understand')}</p>
        </div>
        <SlButton
          variant="danger"
          disabled={!confirmed}
          onClick={() => {
            tokens.map((token) => addToken(token))
            if (handleCurrencySelect) {
              handleCurrencySelect(tokens[0])
            }
          }}
          className=".token-dismiss-button"
        >
          {t('Import')}
        </SlButton>
      </div>
    </AutoColumn>
  )
}

export default ImportToken
